import { useEffect, useRef, useState } from "react";
import { useAudioLevel } from "../hooks/useAudioLevel";

interface VoiceVisualizerProps {
  /** Audio track to visualize - if null, uses random animation */
  audioTrack?: MediaStreamTrack | null;
  /** For local mic visualization without a track */
  participantType?: "local" | "bot";
  barColor?: string;
  backgroundColor?: string;
  barWidth?: number;
  barGap?: number;
  barMaxHeight?: number;
  barCount?: number;
  className?: string;
}

/**
 * Audio visualizer bars - matches @pipecat-ai/client-react VoiceVisualizer
 */
export function VoiceVisualizer({
  audioTrack = null,
  participantType: _participantType,
  barColor = "#71717a",
  backgroundColor = "transparent",
  barWidth = 3,
  barGap = 1,
  barMaxHeight = 24,
  barCount = 5,
  className,
}: VoiceVisualizerProps) {
  const { audioLevel } = useAudioLevel(audioTrack);
  const [bars, setBars] = useState<number[]>(() =>
    Array(barCount)
      .fill(0)
      .map(() => 0.3),
  );
  const frameRef = useRef<number>(0);

  // Animate bars based on audio level
  useEffect(() => {
    let animationId: number;

    const animate = () => {
      setBars((prevBars) =>
        prevBars.map((_, i) => {
          // Base level from audio, with some randomness for visual interest
          const base = audioTrack
            ? 0.3 + audioLevel * 0.7
            : 0.3 + Math.random() * 0.4;
          const variance = Math.sin(Date.now() / 150 + i * 0.8) * 0.15;
          return Math.max(0.2, Math.min(1, base + variance));
        }),
      );
      frameRef.current++;
      animationId = requestAnimationFrame(animate);
    };

    // Slower update rate for performance
    const intervalId = setInterval(() => {
      animationId = requestAnimationFrame(animate);
    }, 50);

    return () => {
      clearInterval(intervalId);
      if (animationId) cancelAnimationFrame(animationId);
    };
  }, [audioLevel, audioTrack]);

  const totalWidth = barCount * barWidth + (barCount - 1) * barGap;

  return (
    <div
      className={`flex items-center justify-center ${className || ""}`}
      style={{
        gap: barGap,
        height: barMaxHeight,
        width: totalWidth,
        background: backgroundColor,
      }}
    >
      {bars.map((barValue, i) => (
        <div
          key={i}
          className="transition-[height] duration-100 ease-out"
          style={{
            width: barWidth,
            height: Math.max(4, barValue * barMaxHeight),
            backgroundColor: barColor,
            borderRadius: barWidth / 2,
          }}
        />
      ))}
    </div>
  );
}
